import Icon from '@amzn/awsui-components-core/icon';
import { applyMode, Mode } from '@amzn/awsui-global-styles';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getEmployeeByLogin } from 'src/api/papi-service';
import { useAskQuick } from 'src/components/ask-quick/AskQuickContext';
import { eLocalStorageKeys, generateProfilePictureUrl } from 'src/constants/AppConstants';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { useAskRino } from 'src/hooks/useAskRino';
import { EmbeddedAppInfo } from 'src/hooks/useEmbeddedApp';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { BADGE_SHADOW_MAP, mapEmployeeToProfileInfo } from 'src/utilities/profile-utils';
import ASK_RINO_ICON from '../../../images/askrino.svg';
import GEN_AI from '../../../images/gen-ai.svg';
import QUICKSUITE_LOGO_ACTIVE from '../../../images/quicksuite-logo-active.svg';
import { useAuth } from '../../context/AuthContextProvider';
import { DFPProfileComponent } from './DFPProfileComponent';
import { ASSISTANT_OPTIONS, PROFILE_DROPDOWN_ITEMS } from './profile-dropdown-constants';
import { SupportDropdown } from './SupportDropdown';
import UserPreferencesModal from './user-preferences/UserPreferencesModal';

interface TopNavActionsProps {
  embeddedApp: EmbeddedAppInfo | null;
  sendMessageToEmbed: (type: string, payload?: Record<string, unknown>) => boolean;
}

export const TopNavActions: React.FC<TopNavActionsProps> = ({ embeddedApp, sendMessageToEmbed }) => {
  const { Alias: userAlias } = useAuth();
  const { togglePanel: toggleAskQuick, closePanel: closeAskQuick, isOpen: isAskQuickOpen } = useAskQuick();
  const { isInstalled: isAskRinoInstalled, open: openAskRino, close: closeAskRino } = useAskRino();
  const [isDarkModeEnabled, setIsDarkModeEnabled] = usePersistedState(eLocalStorageKeys.IsDarkModeEnabled, false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [showAssistantDropdown, setShowAssistantDropdown] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = usePersistedState<string | null>(eLocalStorageKeys.SelectedAssistant, null);
  const [badgeColor, setBadgeColor] = useState<string | null>(null);
  const profilePanelRef = useRef<HTMLDivElement>(null);
  const assistantDropdownRef = useRef<HTMLDivElement>(null);
  const darkModeBeforeAskQuick = useRef<boolean | null>(null);
  const location = useLocation();
  const isEAHuddle = location.pathname.startsWith('/ea-huddle');

  useEffect(() => {
    applyMode(isDarkModeEnabled ? Mode.Dark : Mode.Light);
  }, [isDarkModeEnabled]);

  // Pre-warm profile info cache on page load
  useEffect(() => {
    const prewarmProfileCache = async () => {
      try {
        const response = await getEmployeeByLogin(userAlias, 'job');
        if (!response.employee) return;
        const info = mapEmployeeToProfileInfo(response.employee);
        if (info.badgeColor && BADGE_SHADOW_MAP[info.badgeColor]) {
          setBadgeColor(BADGE_SHADOW_MAP[info.badgeColor]);
        }
      } catch {
        // Best-effort pre-warm; DFPProfileComponent has its own fallback fetch on cache miss
      }
    };
    prewarmProfileCache();
  }, [userAlias]);

  // Close profile panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profilePanelRef.current && !profilePanelRef.current.contains(event.target as Node)) {
        setShowProfilePanel(false);
      }
    };
    if (showProfilePanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfilePanel]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (assistantDropdownRef.current && !assistantDropdownRef.current.contains(event.target as Node)) {
        setShowAssistantDropdown(false);
      }
    };
    if (showAssistantDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssistantDropdown]);

  const handleAssistantSelect = useCallback(
    (assistant: string) => {
      setShowAssistantDropdown(false);

      // Toggle off: clicking the already-active assistant closes it
      if (selectedAssistant === assistant) {
        setSelectedAssistant(null);
        if (assistant === ASSISTANT_OPTIONS.askRino.id) {
          closeAskRino();
        } else {
          closeAskQuick();
        }
        return;
      }

      // Switch: close current, open new
      closeAskRino();
      closeAskQuick();

      setSelectedAssistant(assistant);
      if (assistant === ASSISTANT_OPTIONS.askRino.id) {
        openAskRino();
      } else {
        toggleAskQuick();
      }
    },
    [openAskRino, closeAskRino, toggleAskQuick, closeAskQuick, selectedAssistant, setSelectedAssistant]
  );

  useEffect(() => {
    if (isAskQuickOpen && isDarkModeEnabled) {
      darkModeBeforeAskQuick.current = true;
      setIsDarkModeEnabled(false);
      sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: false });
    } else if (!isAskQuickOpen && darkModeBeforeAskQuick.current) {
      darkModeBeforeAskQuick.current = null;
      setIsDarkModeEnabled(true);
      sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: true });
    }
  }, [isAskQuickOpen, isDarkModeEnabled, setIsDarkModeEnabled, sendMessageToEmbed]);

  return (
    <div className="dfp-top-nav__right">
      {embeddedApp?.url && (
        <>
          <button
            className="dfp-top-nav__utility-btn"
            onClick={() => embeddedApp.url && window.open(embeddedApp.url, '_blank', 'noopener,noreferrer')}
            aria-label={`Open ${embeddedApp.name || 'application'} in a new tab`}
          >
            <span style={{ transform: 'scale(1.15)', display: 'flex', alignItems: 'center', color: '#ffffff' }}>
              <Icon name="external" />
            </span>
          </button>
          <div className="dfp-top-nav__divider-v" />
        </>
      )}
      {isEAHuddle && (
        <>
          <SupportDropdown />
          <div className="dfp-top-nav__divider-v" />
        </>
      )}
      <div className="assistant-dropdown" ref={assistantDropdownRef}>
        <button
          className="dfp-top-nav__utility-btn"
          onClick={() => setShowAssistantDropdown((prev) => !prev)}
          aria-expanded={showAssistantDropdown}
          aria-label="Select assistant"
        >
          <img
            src={
              selectedAssistant === ASSISTANT_OPTIONS.askRino.id
                ? ASK_RINO_ICON
                : selectedAssistant === ASSISTANT_OPTIONS.quickSuite.id
                ? QUICKSUITE_LOGO_ACTIVE
                : GEN_AI
            }
            alt="Assistants"
            className={`dfp-top-nav__quicksuite-logo${selectedAssistant ? ' dfp-top-nav__quicksuite-logo--active' : ''}`}
          />
          <FontAwesomeIcon icon={showAssistantDropdown ? faChevronUp : faChevronDown} className="assistant-dropdown__chevron" />
        </button>
        {showAssistantDropdown && (
          <div className="assistant-dropdown__menu">
            <button className="assistant-dropdown__item" onClick={() => handleAssistantSelect(ASSISTANT_OPTIONS.askRino.id)}>
              <img src={ASK_RINO_ICON} alt="" className="assistant-dropdown__icon" />
              <div className="assistant-dropdown__text">
                <span className="assistant-dropdown__name">{ASSISTANT_OPTIONS.askRino.name}</span>
                <span className="assistant-dropdown__description">{ASSISTANT_OPTIONS.askRino.description}</span>
              </div>
              {!isAskRinoInstalled && <span className="assistant-dropdown__install-hint">Install</span>}
            </button>
            <button className="assistant-dropdown__item" onClick={() => handleAssistantSelect(ASSISTANT_OPTIONS.quickSuite.id)}>
              <img src={QUICKSUITE_LOGO_ACTIVE} alt="" className="assistant-dropdown__icon" />
              <div className="assistant-dropdown__text">
                <span className="assistant-dropdown__name">{ASSISTANT_OPTIONS.quickSuite.name}</span>
                <span className="assistant-dropdown__description">{ASSISTANT_OPTIONS.quickSuite.description}</span>
              </div>
            </button>
          </div>
        )}
      </div>
      <div className="dfp-top-nav__divider-v" />
      <div className="avatar-dropdown" ref={profilePanelRef}>
        <button
          className="avatar-dropdown__button"
          onClick={() => setShowProfilePanel((prev) => !prev)}
          aria-expanded={showProfilePanel}
          aria-label="Toggle profile panel"
        >
          <img
            src={generateProfilePictureUrl(userAlias)}
            alt={userAlias}
            className="avatar-dropdown__image"
            style={badgeColor ? { boxShadow: badgeColor } : undefined}
          />
          <FontAwesomeIcon icon={showProfilePanel ? faChevronUp : faChevronDown} className="avatar-dropdown__chevron" />
        </button>
        {showProfilePanel && (
          <div className="profile-panel">
            <DFPProfileComponent
              profile_items={PROFILE_DROPDOWN_ITEMS}
              onCancel={() => setShowProfilePanel(false)}
              onOpenPreferences={() => setShowPreferencesModal(true)}
            />
          </div>
        )}
      </div>
      <UserPreferencesModal visible={showPreferencesModal} onDismiss={() => setShowPreferencesModal(false)} />
    </div>
  );
};
